import type {
  DeviceStyles,
  DevToolsTone,
  ToneStyle,
} from 'Modules/softphone/components/SoftphoneDevTools/index.types';

export const SURFACE = {
  card: 'overflow-hidden rounded-[10px] border border-zinc-800 bg-zinc-900',
  cardHeader: 'border-b border-zinc-800 px-3 py-2.5',
  inset: 'rounded-[7px] border border-zinc-800 bg-zinc-900',
  divider: 'border-zinc-800',
  code: 'bg-zinc-950',
  empty: 'rounded-[10px] border border-dashed border-zinc-700 bg-zinc-900',
} as const;

export const CARD_TITLE = 'text-[12.5px] font-semibold text-zinc-200';

export const MICRO_LABEL =
  'text-[10px] font-semibold uppercase tracking-[0.07em] text-zinc-400';

export const TONE: Record<DevToolsTone, ToneStyle> = {
  ok: {
    inset: 'rounded-[7px] border border-emerald-800 bg-emerald-950',
    card: 'overflow-hidden rounded-[10px] border border-emerald-800 bg-zinc-900',
    header: 'border-b border-emerald-800 bg-emerald-950 px-3 py-2.5',
    dot: 'bg-emerald-400',
    text: 'text-emerald-400',
  },
  warn: {
    inset: 'rounded-[7px] border border-amber-800 bg-amber-950',
    card: 'overflow-hidden rounded-[10px] border border-amber-800 bg-zinc-900',
    header: 'border-b border-amber-800 bg-amber-950 px-3 py-2.5',
    dot: 'bg-amber-400',
    text: 'text-amber-400',
  },
  bad: {
    inset: 'rounded-[7px] border border-red-800 bg-red-950',
    card: 'overflow-hidden rounded-[10px] border border-red-800 bg-zinc-900',
    header: 'border-b border-red-800 bg-red-950 px-3 py-2.5',
    dot: 'bg-red-400',
    text: 'text-red-400',
  },
  /**
   * Not a state — the absence of one. A reading that is neither good nor bad
   * news must not borrow a colour, or the tinted rows stop meaning anything.
   */
  muted: {
    inset: 'rounded-[7px] border border-zinc-800 bg-zinc-900',
    card: 'overflow-hidden rounded-[10px] border border-zinc-800 bg-zinc-900',
    header: 'border-b border-zinc-800 bg-zinc-900 px-3 py-2.5',
    dot: 'bg-zinc-400',
    text: 'text-zinc-400',
  },
};

/** Inbound is violet, outbound is blue — consistently, in every tab. */
export const DIRECTION = {
  inbound: {
    chip: 'border-violet-800 bg-violet-950 text-violet-400',
    stripe: 'border-l-violet-400',
    ink: 'text-violet-400',
  },
  outbound: {
    chip: 'border-sky-800 bg-sky-950 text-sky-400',
    stripe: 'border-l-sky-400',
    ink: 'text-sky-400',
  },
} as const;

/** Event channels carry the same colour key as the legend under the timeline. */
export const CHANNEL = {
  SIP: 'border-sky-800 bg-sky-950 text-sky-400',
  RTP: 'border-emerald-800 bg-emerald-950 text-emerald-400',
  DEVICE: 'border-amber-800 bg-amber-950 text-amber-400',
  CALL: 'border-violet-800 bg-violet-950 text-violet-400',
} as const;

/**
 * Audio devices carry a colour of their own, one per kind.
 *
 * Not borrowed from `CHANNEL`: every device change is on the DEVICE channel, so
 * that badge says the one thing every such row already shares. The kind is what
 * tells them apart, and it keeps the same colour as a badge and as a lane mark.
 */
export const DEVICE: DeviceStyles = {
  microphone: {
    label: 'Mic',
    mark: 'bg-violet-400',
    detail: 'border-violet-800',
    chip: 'border-violet-800 bg-violet-950 text-violet-400',
  },
  speaker: {
    label: 'Speaker',
    mark: 'bg-cyan-400',
    detail: 'border-cyan-800',
    chip: 'border-cyan-800 bg-cyan-950 text-cyan-400',
  },
  ringer: {
    label: 'Ringer',
    mark: 'bg-amber-400',
    detail: 'border-amber-800',
    chip: 'border-amber-800 bg-amber-950 text-amber-400',
  },
};

/**
 * The 22px square that copies one value.
 *
 * Not a `DevToolsButton` variant: it is a different shape with no label, and
 * the only thing it shares is the focus ring. Its siblings live in
 * `DevToolsButton/index.styles.ts`.
 */
export const BUTTON = {
  icon: 'flex h-[22px] w-[22px] flex-shrink-0 items-center justify-center rounded-[5px] border border-zinc-800 text-zinc-400 transition-colors hover:border-emerald-800 hover:text-emerald-400 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400/40',
} as const;

/** A pill-shaped filter chip. */
export const chipClasses = (isActive: boolean): string =>
  [
    'inline-flex items-center gap-1.5 rounded-[20px] border px-2.5 py-1 text-[11px] font-medium transition-colors',
    'focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400/40',
    isActive
      ? 'border-emerald-800 bg-emerald-950 text-emerald-400'
      : 'border-zinc-800 bg-zinc-900 text-zinc-400 hover:text-zinc-200',
  ].join(' ');
